import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Pencil } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { dkMessage, fmt, fmtQuantity, normKey, unitOf } from "./dkUtils";
import { ItemDialog, groupKeys } from "./ItemDialog";
import { OfflineBox, cardClass } from "./widgets";

const I = OPS.dk.items;

/**
 * «الأصناف» (reviewers): the ten daily key items — name, unit, pack helper,
 * recipe ingredient keys (a key the recipes no longer hold is flagged: that item
 * has no «حسب المبيعات» until it is relinked), the flag minimum, excluded
 * locations and active. Editing opens `ItemDialog`.
 */
export function ItemsAdmin({ scope }) {
    const { t, tn, lang } = useI18n();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [keys, setKeys] = useState([]);
    const [keysLoaded, setKeysLoaded] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setFailed(false);
        try {
            const r = await api.get("/dk/items");
            const list = Array.isArray(r.data) ? r.data : r.data?.items || [];
            setItems([...list].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
        } catch (e) {
            const msg = dkMessage(e, lang);
            if (msg) toast.error(msg); else setFailed(true);
        } finally {
            setLoading(false);
        }
    }, [lang]);

    useEffect(() => { load(); }, [load]);
    // the recipe keys are a separate, optional read: without them the list still works
    useEffect(() => {
        let alive = true;
        api.get("/dk/ingredient-keys")
            .then((r) => {
                if (!alive) return;
                const rows = Array.isArray(r.data) ? r.data : r.data?.keys || r.data?.ingredient_keys || r.data?.items || [];
                setKeys(groupKeys(rows));
                setKeysLoaded(true);
            })
            .catch(() => { if (alive) setKeysLoaded(false); });
        return () => { alive = false; };
    }, []);

    // compared as the server matches them (`_norm_key`), never by raw text
    const known = useMemo(() => new Set(keys.map((g) => normKey(g.key))), [keys]);
    const locName = useMemo(() => {
        const m = new Map((scope?.locations || []).map((l) => [l.code, l]));
        return (code) => (m.get(code) ? tn(m.get(code)) : code);
    }, [scope, tn]);
    const dflt = (it) => (it.unit_class === "pc" ? scope?.settings?.flag_min_pc_milli : scope?.settings?.flag_min_kg_milli);

    return (
        <div data-testid={I.view} className="space-y-3">
            <p className="text-[11px] leading-relaxed text-[color:var(--ink-muted)]">{t("dk_items_intro")}</p>
            {failed ? <OfflineBox onRetry={load} busy={loading} /> : null}
            {loading && !items.length ? <InlineLoader /> : !items.length ? (
                <p data-testid={I.empty}
                   className="rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] py-10 text-center text-sm text-[color:var(--ink-muted)]">
                    {t("dk_no_items")}
                </p>
            ) : (
                <ul className={`${cardClass} divide-y divide-[color:var(--border-soft)] transition-opacity duration-200 ${loading ? "opacity-60" : ""}`}>
                    {items.map((it) => {
                        const unit = unitOf(it, t);
                        const keysN = (it.recipe_ingredient_keys || []).length;
                        const broken = keysLoaded ? (it.recipe_ingredient_keys || []).filter((k) => !known.has(normKey(k))).length : 0;
                        const excluded = it.excluded_location_codes || [];
                        const min = it.flag_min_milli ?? dflt(it);
                        return (
                            <li key={it.id} data-testid={I.row(it.id)} data-active={it.is_active === false ? "false" : "true"}
                                className={`flex items-start gap-2 px-3 py-3 ${it.is_active === false ? "opacity-60" : ""}`}>
                                <div className="min-w-0 flex-1 space-y-1">
                                    <p className="flex flex-wrap items-center gap-1.5 text-sm font-bold text-[color:var(--ink)]">
                                        <span className="min-w-0 break-words">{tn(it)}</span>
                                        <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">{unit}</Badge>
                                        {it.is_active === false ? <Badge variant="warning" className="px-1.5 py-0 text-[10px]">{t("dk_inactive")}</Badge> : null}
                                    </p>
                                    <p className="text-[11px] leading-snug text-[color:var(--ink-faint)]">
                                        {it.sc_item_code ? <bdi dir="ltr">{it.sc_item_code}</bdi> : null}
                                        {it.sc_item_code && !it.sc_item_id ? <span className="text-[color:var(--warning-soft-fg)]"> · {t("dk_items_sc_unlinked")}</span> : null}
                                        {it.pack_unit && it.pack_size ? <> · <bdi>{fmt(t("dk_pack_hint"), { pack: it.pack_unit, n: fmtQuantity(it.pack_size, lang), unit })}</bdi></> : null}
                                    </p>
                                    <p className="text-[11px] leading-snug text-[color:var(--ink-muted)]">
                                        {fmt(t("dk_items_keys_n"), { n: fmtQuantity(keysN, lang) })}
                                        {min !== null && min !== undefined ? (
                                            <> · {fmt(t("dk_items_min_short"), { n: fmtQuantity(min / 1000, lang), unit })}
                                                {it.flag_min_milli !== null && it.flag_min_milli !== undefined ? ` (${t("dk_items_min_own")})` : ""}</>
                                        ) : null}
                                        {excluded.length ? <> · {fmt(t("dk_items_excluded_short"), { list: excluded.map(locName).join("، ") })}</> : null}
                                    </p>
                                    {!keysN || broken ? (
                                        <p className="flex items-center gap-1 text-[11px] font-semibold text-[color:var(--warning-soft-fg)]">
                                            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                            {!keysN ? t("dk_items_no_link") : fmt(t("dk_items_broken_n"), { n: fmtQuantity(broken, lang) })}
                                        </p>
                                    ) : null}
                                </div>
                                <Button data-testid={I.edit(it.id)} variant="ghost" size="sm" onClick={() => setEditing(it)}
                                        aria-label={t("dk_items_edit")} className="h-9 shrink-0 gap-1 px-2 text-[11px]">
                                    <Pencil className="h-3.5 w-3.5" />
                                    <span className="hidden sm:inline">{t("dk_items_edit")}</span>
                                </Button>
                            </li>
                        );
                    })}
                </ul>
            )}
            {editing ? (
                <ItemDialog item={editing} scope={scope} keys={keys} keysLoaded={keysLoaded}
                            onClose={() => setEditing(null)}
                            onSaved={() => { setEditing(null); load(); }} />
            ) : null}
        </div>
    );
}
